import { MouseEventHandler, useEffect, useId, useRef, useState } from 'react';
import classNames from 'classnames';
import { DropdownItemProps } from '@components/dropdown/DropdownItem';
import { DropdownList } from '@components/dropdown/DropdownList';
import { NavItem, NavItemProps } from '@components/nav/NavItem';
import { useDropdownKeyboardNavigation } from './hooks';
import './DropdownTemplate.scss';

export interface DropdownTemplateProps {
  /** NaveItemProps Object used to build the dropdown anchor point */
  anchorProps: NavItemProps;
  className?: string;
  /** array of objects to pass to DropdownList */
  dropdownItems?: Array<DropdownItemProps>;
  /** sets style of DropdownList items */
  isMobile?: boolean;
  /** used to allow mobile to toggle the dropdown open and closed */
  toggleClick?: boolean;
  /** accessibility label for dropdown nav item */
  ariaLabel?: string;
  /**
   * ARIA role for the dropdown list. Undefined by default (no role applied).
   * Pick the one that fits the use case:
   *   - "menu" for an action list like a profile dropdown. Items become `menuitem`.
   *   - "listbox" for a value picker with non-link items. Items become `option`.
   * The trigger's aria-haspopup is set to match automatically. A per-item `role`
   * in `dropdownItems` still overrides the derived item role.
   * For items with `href` use "menu" or leave undefined; an `option` that is a link is invalid ARIA.
   */
  role?: 'menu' | 'listbox';
  linkComponent?: React.ElementType;
}

/**
 * DropdownTemplate component
 * @param anchorProps - NaveItemProps Object used to build the dropdown anchor point
 * @param className - optional class
 * @param dropdownItems - array of objects to pass to DropdownList
 * @param isMobile - sets style of DropdownList items
 * @param toggleClick - used to allow mobile to toggle the dropdown open and closed
 * @param ariaLabel - accessibility label for dropdown nav item
 * @param role - ARIA role for the dropdown list ('menu' or 'listbox'). default undefined (no role). sets appropriate item roles and trigger aria-haspopup.
 * @param linkComponent - Custom component to render links instead of a native `<a>` (e.g. Next.js Link, React Router Link).
 * @constructor
 */
export const DropdownTemplate: React.FC<DropdownTemplateProps> = ({
  anchorProps,
  className,
  dropdownItems = [],
  isMobile,
  toggleClick,
  ariaLabel,
  role,
  linkComponent,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Until hydrated, CSS reveals the list on hover / focus, so SSR markup must not claim a disclosure state
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => setIsHydrated(true), []);

  const anchorRef = useRef<HTMLDivElement>(null);
  const dropdownListRef = useRef<HTMLUListElement | null>(null);

  const { onKeyDown, onBlur } = useDropdownKeyboardNavigation({
    isOpen,
    setIsOpen,
    toggleClick,
    itemCount: dropdownItems.length,
    anchorRef,
    listRef: dropdownListRef,
  });

  const onMouseOverHandler: MouseEventHandler = () => {
    if (!toggleClick) setIsOpen(true);
  };
  const onMouseOutHandler: MouseEventHandler = () => {
    if (!toggleClick) setIsOpen(false);
  };

  let onClick;
  if (toggleClick) {
    onClick = () => {
      setIsOpen((prevState: boolean) => !prevState);
    };
  }

  const listId = useId();
  const itemRole = role === 'menu' ? 'menuitem' : role === 'listbox' ? 'option' : undefined;
  const itemsWithRole = itemRole
    ? dropdownItems.map(item => ({ ...item, role: item.role ?? itemRole }))
    : dropdownItems;
  // When the trigger renders as a focusable <div> (no href), give it role="button"
  // so screen readers announce it as an interactive control. Skip for the <a> branch
  // since anchors have their own native semantics. Explicit anchorProps.role wins.
  const triggerRole = anchorProps.role ?? (anchorProps.href ? undefined : 'button');

  return (
    <div
      className={classNames(
        'vds-dropdown-template',
        {
          'vds-dropdown-template--mobile': isMobile,
          'vds-dropdown-template--hydrated': isHydrated,
        },
        className,
      )}
      onMouseOver={onMouseOverHandler}
      onMouseOut={onMouseOutHandler}
      onClick={onClick}
      onBlur={onBlur}
      onKeyDown={onKeyDown}>
      <NavItem
        className="vds-dropdown-template__anchor"
        {...anchorProps}
        {...(linkComponent && { as: linkComponent })}
        dropdown
        isOpen={isOpen}
        ref={anchorRef}
        size="small"
        role={triggerRole}
        ariaLabel={ariaLabel ?? anchorProps.ariaLabel}
        aria-expanded={isHydrated ? isOpen : undefined}
        aria-controls={listId}
        aria-haspopup={role}></NavItem>
      <DropdownList
        className="vds-dropdown-template__list"
        id={listId}
        ref={dropdownListRef}
        size={isMobile ? 'small' : 'large'}
        items={itemsWithRole}
        isOpen={isOpen}
        role={role}
        linkComponent={linkComponent}
      />
    </div>
  );
};
